import * as Sentry from '@sentry/vue'
import auth from 'shared/plugins/auth'
import {getHostedWebAppVersion} from 'shared/plugins/api'
import sentryOptions from './sentryOptions'

export function sentryInit(app) {
	if (Sentry.isInitialized()) {
		throw new Error('Sentry cannot be initialized twice, because it can override options.')
	}

	if (import.meta.env.PROD) {
		Sentry.init({
			...sentryOptions,
			integrations: function (defaultIntegrations) {
				// Don't de-duplicate events.
				// De-duplication was leading to swallowed error notifications
				// for errors with `.notification` property,
				// like user-triggered API calls failing with an error message.
				return defaultIntegrations.filter(integration => integration.name !== 'Dedupe')
			},
			app,
			beforeSend: async function (event, hint) {
				// Don't send the test-errors generated by the onboarding demo page to Sentry
				if (location.href.includes('/complete-signup/ready')) return null

				// Reload app if it failed to fetch dynamically imported module, as that can happen when outdated client bundles keep running
				// e.g. the hashes of the same bundles will have changed, and loading the bundles with old hashes will fail
				const asyncModuleImportError = 'Failed to fetch dynamically imported module'
				const asyncCssPreloadError = 'Unable to preload CSS for'
				if (
					hint?.originalException?.message.includes(asyncModuleImportError) ||
					hint.originalException?.message.includes(asyncCssPreloadError)
				) {
					// Call web app server, so that it drops the calling client's cache via the `Clear-Site-Data` header.
					await getHostedWebAppVersion()
					// Reload, with empty cache.
					window.location.reload()
					return null
				}

				if (event.level === 'error') {
					const exception = hint.originalException

					// Add the Bird Eats Bug session link to the payload, if it exists.
					const sessionLink = window.birdeatsbug?.session?.link || window.birdeatsbugExtension?.session?.link
					if (exception && sessionLink) {
						if (!exception.contexts) exception.contexts = {}
						exception.contexts['Bird Eats Bug'] = {url: sessionLink}
					}

					// strigify context's properties
					if (exception?.contexts) {
						const {default: transformToSentryContext} = await import(
							'shared/utils/sentryContextTransformer'
						)
						// NOTE: patching through context like this seems to have smaller size restrictions of payload compared to
						// Sentry.captureException(error, {contexts})
						event.contexts = transformToSentryContext(exception.contexts)

						delete hint.originalException.contexts
					}

					// on error, enable SDK instant replay, in case the user reports the issue
					const {
						default: initializeSdk,
						sharedSdkUiOptions,
						defaultPublicAppId,
					} = await import('shared/utils/birdSdk')
					initializeSdk({ui: sharedSdkUiOptions, instantReplay: true, publicAppId: defaultPublicAppId})

					if (exception?.notification) {
						const {generateCamelCasedBugName} = await import('shared/utils/generateBugName')
						event.tags = {
							...(event.tags || {}),
							errorId: `${generateCamelCasedBugName()}-${crypto.randomUUID()}`,
						}
						const notification = exception?.notification || {}
						let {title, message} = notification
						let dangerouslyUseHTMLString = false
						if (message) {
							message = `${message}

					ID: ${event.tags.errorId}`
						} else {
							message = `In case something doesn't work, try reloading the page. If the problem persists, please contact <span class="link-blue">our support</span>.<br>
							<strong>ID: ${event.tags.errorId}</strong>`

							dangerouslyUseHTMLString = true
						}

						const {useNotifyStore} = await import('shared/stores/notify')
						const notify = useNotifyStore()
						notify.error({
							title: title || 'An error has occurred',
							message,
							dangerouslyUseHTMLString,
							onClick: () => {
								const sdkOptions = {
									hooks: {
										beforeUpload: sessionData => {
											sessionData.session.title = `Error ID ${event.tags.errorId}`
											return sessionData
										},
									},
								}
								if (auth.user.value?.email) {
									sdkOptions.user = {email: auth.user.value.email}
								}
								window.birdeatsbug.setOptions(sdkOptions)
								window.birdeatsbug.trigger()
							},
						})
					}
				}

				return event
			},
		})
	}
}
